import { User } from "firebase/auth";
import { apiFetch } from "./client";

export type SyndicatePotKind = "CLUB" | "PLAYER";
export type SyndicatePotStatType = "GOALS" | "ASSISTS" | "YELLOW_CARDS" | "RED_CARDS";

export interface SyndicateClubInfo {
  id: string;
  name: string;
  shortName: string | null;
}

export interface SyndicateExcludedPlayerInfo {
  id: string;
  name: string;
  club: string;
}

export interface SyndicatePot {
  id: string;
  order: number;
  kind: SyndicatePotKind;
  label: string;
  // CLUB pots
  winPoints: number | null;
  drawPoints: number | null;
  eligibleClubs?: SyndicateClubInfo[];
  // PLAYER pots
  statType: SyndicatePotStatType | null;
  pointsPer: number | null;
  // Players barred from this pot by the admin — the picker excludes these
  // from its results server-side; a pre-existing pick for one of these IDs is
  // still returned (and flagged `invalid`) rather than silently dropped.
  excludedPlayers?: SyndicateExcludedPlayerInfo[];
}

export interface SyndicatePick {
  potId: string;
  clubId: string | null;
  club: SyndicateClubInfo | null;
  playerId: string | null;
  player: { id: string; name: string; club: string; position: string | null } | null;
  points: number;
  /** This pot's contribution to the single most recent scoring gameweek — the itemised
   * version of the response's overall `myDelta`. */
  potDelta: number;
  /** CLUB pots: chronological W/D/L, capped to the last 5 results, `[]` if none yet. `null` for
   * a PLAYER pick (or a pick with no matching pot). */
  form: ("W" | "D" | "L")[] | null;
  /** PLAYER pots: raw stat count (e.g. total goals) over the scoring window, `0` if none yet.
   * `null` for a CLUB pick (or a pick with no matching pot). */
  statCount: number | null;
  invalid?: boolean;
}

export interface SyndicatePicksResponse {
  isLocked: boolean;
  pots: SyndicatePot[];
  picks: SyndicatePick[];
  tiebreaker: number | null;
  totalScore: number;
  myDelta: number;
  startGwNumber: number;
  // Only present when this response has been merged with a member-view fetch
  // (see SyndicatePage.tsx's load()) — the league-level GET never returns it.
  rankHistory?: RankPoint[];
}

export interface RankPoint {
  gwNumber: number;
  gwName: string;
  gwShortName: string;
  rank: number;
  score: number;
}

export interface SyndicateMemberPicksResponse {
  isLocked: boolean;
  picks: SyndicatePick[];
  tiebreaker: number | null;
  totalScore: number;
  myDelta: number;
  rankHistory: RankPoint[];
}

export function getSyndicatePicks(user: User, leagueId: string): Promise<SyndicatePicksResponse> {
  return apiFetch(user, `/leagues/${leagueId}/syndicate/picks`);
}

/** Saves only the pots/tiebreaker included in `picks`/`tiebreaker` — a partial
 * submission (e.g. fixing one pot after an admin exclusion) never needs to
 * resend every other pot. Omit `tiebreaker` to leave it unchanged. */
export function saveSyndicatePicks(
  user: User,
  leagueId: string,
  picks: { potId: string; clubId?: string | null; playerId?: string | null }[],
  tiebreaker?: number,
): Promise<{ saved: boolean }> {
  return apiFetch(user, `/leagues/${leagueId}/syndicate/picks`, {
    method: "PUT",
    body: JSON.stringify({ picks, ...(tiebreaker !== undefined ? { tiebreaker } : {}) }),
  });
}

export function getSyndicateMemberPicks(
  user: User,
  leagueId: string,
  memberId: string,
): Promise<SyndicateMemberPicksResponse> {
  return apiFetch(user, `/leagues/${leagueId}/syndicate/member/${memberId}/picks`);
}
